import * as React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ErrorOutline from '@mui/icons-material/ErrorOutline'
import { useTheme } from '@mui/material/styles'
import { COARSE_POINTER, TOUCH_TARGET_PX } from '../touch'

type Props = {
  source: string
  label?: string
  message?: string
  /** Only a truncation warning: some events were left out, so Retry can't help. */
  truncated?: boolean
  onRetry?: () => void
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

/** A failed source with no chip of its own (e.g. Outlook) still has to be surfaced. */
export function SourceUnavailableBadge({
  source,
  label,
  message,
  truncated,
  onRetry,
}: Props) {
  const theme = useTheme()
  const name = label ?? capitalize(source)

  return (
    <Box
      role="status"
      data-test-id="source-unavailable"
      title={message}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        fontFamily: theme.typography.fontFamily,
        fontSize: '12px',
        color: theme.palette.text.secondary,
      }}
    >
      <ErrorOutline
        aria-hidden
        sx={{ fontSize: 16, color: theme.palette.error.main }}
      />
      {truncated
        ? `${name}: some events weren't included`
        : `${name} couldn't load`}
      {onRetry && (
        <Button
          size="small"
          variant="text"
          onClick={onRetry}
          data-test-id="source-unavailable-retry"
          aria-label={`Retry loading ${name}`}
          sx={{
            minWidth: 0,
            px: 0.5,
            fontSize: '12px',
            textTransform: 'none',
            [COARSE_POINTER]: { minHeight: `${TOUCH_TARGET_PX}px` },
          }}
        >
          Retry
        </Button>
      )}
    </Box>
  )
}
